import React, {useState} from 'react';

import cs from 'classnames';
import lodashGet from 'lodash/get';

import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import StateButton from 'app/general/widgets/StateButton';
import {useNotifications} from 'app/toasts/hooks';
import {gt} from 'app/utils/i18n';

type Props = {
  fieldValue: string;
  canEdit: boolean;
  updateField: (value: string) => Promise<any>;
  editTitle: string;
  editPlaceholder: string;
  badges?: React.ReactNode;
  inputClass?: string;
  editButtonInputClass?: string;
  fieldName?: string;
  isInline?: boolean;
};

export const GeneralEditField = (props: Props) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const handleFieldChange = (event) => setValue(event.target.value);

  const {showToastNotification} = useNotifications();

  const handleUpdate = (value: string) => {
    setIsUpdating(true);
    const fieldName =
      props.fieldName != null ? props.fieldName : 'non_field_errors';
    props.updateField(value).then(
      () => {
        setIsEditing(false);
        setIsUpdating(false);
      },
      (error) => {
        setIsUpdating(false);
        showToastNotification(
          lodashGet(
            error,
            `data.${fieldName}[0]`,
            gt('Unable to modify this field')
          ),
          {level: 'error'}
        );
      }
    );
  };

  return isEditing === false ? (
    <div>
      {props.fieldValue} {props.badges}
      {props.canEdit ? (
        <ClickToEditButton
          onClick={() => {
            setIsEditing(true);
            setValue(props.fieldValue);
            setIsUpdating(false);
          }}
          className={props.editButtonInputClass}
          tooltip={props.editTitle}
        />
      ) : null}
    </div>
  ) : (
    <div className={`${props.isInline ? 'input-group' : 'flex-grow-1 mb-3'}`}>
      <input
        type="text"
        autoFocus={true}
        name="field"
        className={cs(
          'form-control',
          {'mb-2': props.isInline !== true},
          props.inputClass
        )}
        placeholder={props.editPlaceholder}
        onChange={handleFieldChange}
        value={value}
      />
      <div
        className={`${
          props.isInline ? 'input-group-append' : 'd-flex justify-content-end'
        }`}
      >
        <button
          disabled={isUpdating}
          className={cs('btn btn-secondary', {'mr-1': props.isInline !== true})}
          onClick={() => setIsEditing(false)}
        >
          {gt('Cancel')}
        </button>
        <StateButton
          className="btn btn-primary"
          isIdle={!isUpdating}
          busyText={gt('Saving...')}
          disabled={value === props.fieldValue}
          onClick={() => handleUpdate(value)}
        >
          {gt('Save')}
        </StateButton>
      </div>
    </div>
  );
};
